/**
 * NotificationsPage
 * หน้าเต็มสำหรับแสดง notifications ทั้งหมด พร้อม pagination และ filter
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BellIcon, CheckIcon, TrashIcon, FunnelIcon } from '@heroicons/react/24/outline'
import NotificationItem from '../components/notifications/NotificationItem'
import { useNotifications } from '../hooks/useNotifications'
import { notificationService } from '../services/notificationService'
import { Notification } from '../types/notification'
import SEO from '../components/common/SEO'

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate()
  const {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearError
  } = useNotifications()

  const [currentPage, setCurrentPage] = useState(1)
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)
  const perPage = 20

  // Load notifications เมื่อ component mount หรือเมื่อ filter เปลี่ยน
  useEffect(() => {
    fetchNotifications(currentPage, perPage, showUnreadOnly)
  }, [currentPage, showUnreadOnly, fetchNotifications])

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }

    // Navigate to link
    if (notification.link) {
      const path = notificationService.parseNotificationLink(notification.link)
      navigate(path)
    }
  }

  // Handle delete notification
  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation()
    if (confirm('คุณต้องการลบการแจ้งเตือนนี้หรือไม่?')) {
      await deleteNotification(notificationId)
    }
  }

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  // Toggle unread filter
  const toggleUnreadFilter = () => {
    setShowUnreadOnly(!showUnreadOnly)
    setCurrentPage(1) // Reset to first page
  }

  // Group notifications by date
  const groupByDate = (notifications: Notification[]) => {
    const groups: { [key: string]: Notification[] } = {}

    // Handle undefined or null notifications
    if (!notifications || !Array.isArray(notifications)) {
      return groups
    }

    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    notifications.forEach(notification => {
      const notifDate = new Date(notification.created_at)
      const notifDateStr = notifDate.toDateString()
      const todayStr = today.toDateString()
      const yesterdayStr = yesterday.toDateString()

      let groupKey: string
      if (notifDateStr === todayStr) {
        groupKey = 'วันนี้'
      } else if (notifDateStr === yesterdayStr) {
        groupKey = 'เมื่อวาน'
      } else {
        groupKey = new Intl.DateTimeFormat('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(notifDate)
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(notification)
    })

    return groups
  }

  const groupedNotifications = groupByDate(notifications)

  return (
    <>
      <SEO
        title="การแจ้งเตือน - ไทยซีเคอริตี้ไดเรกทอรี่"
        description="ดูการแจ้งเตือนทั้งหมดของคุณ"
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
        <div className="container-wrapper">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                  <BellIcon className="w-8 h-8 text-blue-600" />
                  <span>การแจ้งเตือน</span>
                </h1>
                <p className="text-gray-600 mt-2">ดูการแจ้งเตือนทั้งหมดของคุณ</p>
              </div>

              <div className="flex items-center space-x-3">
                {/* Filter Toggle */}
                <button
                  onClick={toggleUnreadFilter}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                    ${showUnreadOnly
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <FunnelIcon className="w-4 h-4" />
                  <span>{showUnreadOnly ? 'ทั้งหมด' : 'ยังไม่อ่าน'}</span>
                </button>

                {/* Mark All as Read */}
                {notifications.length > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-all duration-200"
                  >
                    <CheckIcon className="w-4 h-4" />
                    <span>อ่านทั้งหมด</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start justify-between">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={clearError}
                  className="text-red-600 hover:text-red-700 font-medium text-sm"
                >
                  ปิด
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <BellIcon className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {showUnreadOnly ? 'ไม่มีการแจ้งเตือนที่ยังไม่ได้อ่าน' : 'ไม่มีการแจ้งเตือน'}
              </h3>
              <p className="text-gray-600">
                {showUnreadOnly
                  ? 'คุณได้อ่านการแจ้งเตือนทั้งหมดแล้ว'
                  : 'ยังไม่มีการแจ้งเตือนในขณะนี้'}
              </p>
            </div>
          )}

          {/* Notifications List (Grouped by Date) */}
          {!loading && notifications.length > 0 && (
            <div className="space-y-8">
              {Object.entries(groupedNotifications).map(([dateLabel, notifs]) => (
                <div key={dateLabel}>
                  {/* Date Header */}
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="bg-gray-100 px-3 py-1 rounded-lg">{dateLabel}</span>
                    <span className="ml-3 text-sm text-gray-500 font-normal">
                      {notifs.length} รายการ
                    </span>
                  </h2>

                  {/* Notifications */}
                  <div className="space-y-3">
                    {notifs.map((notification) => (
                      <div key={notification.id} className="relative group">
                        <NotificationItem
                          notification={notification}
                          onClick={handleNotificationClick}
                          className="shadow-sm"
                        />

                        {/* Delete Button */}
                        <button
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                          className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 hover:text-red-700"
                          title="ลบการแจ้งเตือน"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination (Optional - if backend supports it) */}
          {/* TODO: Add pagination controls if needed */}
        </div>
      </div>
    </>
  )
}

export default NotificationsPage
